import React, { useState } from 'react';
import { ArrowLeftRight, Plus, Trash2 } from 'lucide-react';

const DataMappingTools = ({ mappings, onAddMapping, onDeleteMapping }) => {
  const [showForm, setShowForm] = useState(false);
  const [newMapping, setNewMapping] = useState({ sourceService: '', sourceField: '', targetService: '', targetField: '', transform: 'direct' });

  const services = ['GoHighLevel', 'AI Dialer', 'Resend Email', 'Twilio SMS', 'Lead Sources', 'Supabase DB'];
  const transforms = ['direct', 'uppercase', 'lowercase', 'trim', 'format_phone', 'format_email', 'custom'];

  const handleAdd = () => {
    if (newMapping?.sourceField && newMapping?.targetField) {
      onAddMapping?.(newMapping);
      setNewMapping({ sourceService: '', sourceField: '', targetService: '', targetField: '', transform: 'direct' });
      setShowForm(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <ArrowLeftRight className="w-5 h-5 text-teal-600" />
          <h2 className="text-base font-semibold text-gray-900">Cross-Service Data Mapping</h2>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
        >
          <Plus className="w-4 h-4" />
          Add Mapping
        </button>
      </div>
      {showForm && (
        <div className="px-6 py-4 bg-teal-50 border-b border-teal-100">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Source Service</label>
              <select
                value={newMapping?.sourceService}
                onChange={e => setNewMapping(p => ({ ...p, sourceService: e?.target?.value }))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select...</option>
                {services?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Source Field</label>
              <input
                type="text"
                placeholder="e.g. firstName"
                value={newMapping?.sourceField}
                onChange={e => setNewMapping(p => ({ ...p, sourceField: e?.target?.value }))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Transform</label>
              <select
                value={newMapping?.transform}
                onChange={e => setNewMapping(p => ({ ...p, transform: e?.target?.value }))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                {transforms?.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Target Service</label>
              <select
                value={newMapping?.targetService}
                onChange={e => setNewMapping(p => ({ ...p, targetService: e?.target?.value }))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="">Select...</option>
                {services?.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Target Field</label>
              <input
                type="text"
                placeholder="e.g. contact_name"
                value={newMapping?.targetField}
                onChange={e => setNewMapping(p => ({ ...p, targetField: e?.target?.value }))}
                className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={handleAdd} className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">Save Mapping</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-white border border-gray-300 text-gray-600 rounded-lg text-sm hover:bg-gray-50 transition-colors">Cancel</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Source</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transform</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {mappings?.map((m) => (
              <tr key={m?.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-6 py-3">
                  <div className="text-xs text-gray-500">{m?.sourceService}</div>
                  <div className="text-sm font-medium text-gray-900 font-mono">{m?.sourceField}</div>
                </td>
                <td className="px-6 py-3">
                  <span className="px-2 py-1 bg-teal-100 text-teal-700 rounded text-xs font-medium">{m?.transform}</span>
                </td>
                <td className="px-6 py-3">
                  <div className="text-xs text-gray-500">{m?.targetService}</div>
                  <div className="text-sm font-medium text-gray-900 font-mono">{m?.targetField}</div>
                </td>
                <td className="px-6 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    m?.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>{m?.active ? 'Active' : 'Inactive'}</span>
                </td>
                <td className="px-6 py-3 text-right">
                  <button onClick={() => onDeleteMapping?.(m?.id)} className="text-gray-400 hover:text-red-500 transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DataMappingTools;
